import React, { useState } from 'react';
import { Pill, num, str } from '../ipintel/shared';
import { AdminLink } from '../adminNav';

/**
 * Vocabulary and plumbing for /admin → Horizon data (HorizonPanel).
 *
 * Everything reads through the panel dispatcher, section "horizon"
 * (Backend/admin_horizon.py): overview, technologies, tech, results, result,
 * runs, prompts, pending, source_save, refresh_current; and the admin-started
 * runs of the batch runner (Backend/horizon_jobs.py): run_plan, run_start,
 * run_job. The Excel export is a raw admin route (EXPORT_PATH,
 * admin_horizon_routes.py) because JSON cannot carry a workbook.
 *
 * The data: horizon_sandbox_prompts / _runs / _results (every sandbox prompt,
 * run and result ever made) and horizon_current — one row per technology,
 * the result the public tech pages show (Backend/horizon/store.py).
 */

export const S = 'horizon';

/** GET ?kind=technologies|results|pending|runs|prompts plus the tab's filters → .xlsx. */
export const EXPORT_PATH = '/api/v1/admin/horizon/export.xlsx';

/** Public tech pages live here (the contract: open them on the production site). */
export const PUBLIC_ORIGIN = 'https://universitytech.io';

export const publicUrl = (path: any): string => {
  const p = str(path).trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  return `${PUBLIC_ORIGIN}${p.startsWith('/') ? p : `/${p}`}`;
};

// ---------------------------------------------------------------------------
// The classification vocabulary — as horizon/prompts/master_prompt.md defines it.
// ---------------------------------------------------------------------------
export const HORIZONS = ['H1', 'H2', 'H3'];
export const HORIZON_NAMES: Record<string, string> = {
  H1: 'Specific application', H2: 'Multi-application', H3: 'Sector-transforming breakthrough',
};
export const horizonLabel = (h: any): string => {
  const k = str(h).toUpperCase();
  return HORIZON_NAMES[k] ? `${k} · ${HORIZON_NAMES[k]}` : (str(h) || '—');
};

export const CONFIDENCES = ['High', 'Medium', 'Low'];

export const LEVERS = ['Performance', 'Productivity & Scale', 'Economics', 'Risk & Resilience', 'Sustainability'];

/** The prompt's closed industry list (the "industry" a result carries). */
export const HZ_INDUSTRIES = [
  'Advanced Materials & Manufacturing', 'Agriculture & Food', 'Construction & Smart Cities',
  'Consumer Goods & Services', 'Creative Works & Copyright', 'Education, HR & Training',
  'Electronics, IoT & Robotics', 'Energy & Sustainability', 'Healthcare & Life Sciences', 'ICT, AI & Computing',
  'Infrastructure, Logistics & Utilities', 'Safety, Defense & First Responders', 'Science, Exploration & Space',
  'Transportation & Mobility', 'Others',
];

// ---------------------------------------------------------------------------
// Parsing — lists may arrive parsed or as the stored "; "-joined text.
// ---------------------------------------------------------------------------
export type Lever = { lever: string; tag: string };

const splitLever = (s: string): Lever => {
  const parts = s.split(/\s*(?:->|→)\s*/);
  return parts.length > 1
    ? { lever: parts[0].trim(), tag: parts.slice(1).join(' ').trim() }
    : { lever: '', tag: s.trim() };
};

/** [{lever, tag}] out of a parsed list, ["Lever -> Tag"] or "Lever -> Tag; Lever -> Tag". */
export const leversOf = (v: any): Lever[] => {
  let out: Lever[] = [];
  if (Array.isArray(v)) {
    out = v.map(x => (typeof x === 'string' ? splitLever(x)
      : { lever: str(x?.lever ?? x?.value_lever), tag: str(x?.tag ?? x?.technical_tag) }));
  } else if (typeof v === 'string' && v.trim()) {
    out = v.split(/;|\n/).map(s => s.trim()).filter(Boolean).map(splitLever);
  }
  return out.filter(x => x.lever || x.tag);
};

/** Example applications: a list, or the stored "; "-joined text. */
export const appsOf = (v: any): string[] => {
  if (Array.isArray(v)) return v.map(x => str(x).trim()).filter(Boolean);
  if (typeof v === 'string' && v.trim()) return v.split(/;\s*|\n+/).map(s => s.replace(/^[-•*\d.)\s]+/, '').trim()).filter(Boolean);
  return [];
};

export type Current = {
  horizon: string; confidence: string; industry: string; problem: string; applications: string[];
  levers: Lever[]; reasoning: string; prompt_id: string; run_id: string; result_id: string; created_at: string;
};

/** horizon.store.current_for(), read defensively (either the parsed or the column names). */
export const normCurrent = (h: any): Current | null => {
  if (!h || typeof h !== 'object') return null;
  const c: Current = {
    horizon: str(h.horizon),
    confidence: str(h.confidence ?? h.horizon_confidence),
    industry: str(h.industry ?? h.horizon_industry),
    problem: str(h.problem ?? h.inferred_problem),
    applications: appsOf(h.applications ?? h.example_applications),
    levers: leversOf(h.levers ?? h.value_lever_tags),
    reasoning: str(h.reasoning),
    prompt_id: str(h.prompt_id),
    run_id: str(h.run_id),
    result_id: str(h.result_id ?? h.id),
    created_at: str(h.result_created_at ?? h.created_at),
  };
  return c.horizon || c.problem || c.levers.length || c.applications.length ? c : null;
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
export const shortId = (id: any, n = 8): string => str(id).slice(0, n);

export const fmtCost = (v: any, small = false): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = num(v);
  return `$${small && n < 1 ? n.toFixed(4) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const fmtN = (v: any): string =>
  (v === null || v === undefined || v === '' ? '—' : num(v).toLocaleString());

export const pct = (a: any, b: any): string => {
  const d = num(b);
  if (!d) return '—';
  const p = (num(a) / d) * 100;
  return `${p >= 99.95 || p === 0 ? p.toFixed(0) : p.toFixed(1)}%`;
};

export const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** ["", ...options] for a Select, plus the "any" label. */
export const opts = (list: string[], any: string) =>
  ({ options: ['', ...list], labels: { '': any } as Record<string, string> });

// ---------------------------------------------------------------------------
// Prompts — names for ids, the "final" marker.
// ---------------------------------------------------------------------------
export type PromptInfo = { id: string; name: string; created_at: string; is_final: boolean; priority_index: number | null };

export const promptList = (rows: any[]): PromptInfo[] => rows.map(r => ({
  id: str(r?.id), name: str(r?.name) || shortId(r?.id), created_at: str(r?.created_at),
  is_final: !!r?.is_final,
  priority_index: r?.priority_index === null || r?.priority_index === undefined || r?.priority_index === ''
    ? null : num(r.priority_index),
})).filter(p => p.id);

/** "Name (26 Jun)" — prompts share names, so the date tells them apart. */
export const promptLabel = (p: PromptInfo | undefined, id?: any): string => {
  if (!p) return shortId(id) || '—';
  const d = p.created_at ? new Date(p.created_at) : null;
  const when = d && !isNaN(d.getTime()) ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '';
  return when ? `${p.name} (${when})` : p.name;
};

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------
export const HorizonPill: React.FC<{ h: any; long?: boolean }> = ({ h, long }) => {
  const k = str(h).toUpperCase();
  if (!k) return <span className="text-grayscale-1100">—</span>;
  const tone = k === 'H3' ? 'good' : k === 'H2' ? 'info' : k === 'H1' ? 'neutral' : 'warn';
  return <Pill tone={tone} title={horizonLabel(k)}>{long ? horizonLabel(k) : k}</Pill>;
};

export const ConfidencePill: React.FC<{ c: any }> = ({ c }) => {
  const v = str(c);
  if (!v) return <span className="text-grayscale-1100">—</span>;
  const t = v.toLowerCase();
  return <Pill tone={t === 'high' ? 'good' : t === 'low' ? 'warn' : 'neutral'} title="Horizon confidence">{v}</Pill>;
};

/** Has data / Pending — whether the public tech page has Horizon data to show. */
export const StatusPill: React.FC<{ status: any; horizon?: any; noSummary?: boolean }> = ({ status, horizon, noSummary }) => {
  const s = str(status);
  if (s === 'has') {
    return <Pill tone="good" title="A usable Horizon result exists — the tech page shows it">
      {horizon ? `Has ${str(horizon)}` : 'Has data'}</Pill>;
  }
  if (s === 'pending') {
    return noSummary
      ? <Pill tone="bad" title="No usable Horizon result, and no summary — the runner never sends a technology without one">Pending · no summary</Pill>
      : <Pill tone="warn" title="No usable Horizon result yet — picked up by the next pending run">Pending</Pill>;
  }
  return <span className="text-grayscale-1100">—</span>;
};

export const LeverChips: React.FC<{ levers: Lever[]; max?: number; compact?: boolean; total?: number }> =
  ({ levers, max, compact, total }) => {
  if (!levers.length) return <span className="text-grayscale-1100 text-xs">—</span>;
  const all = Math.max(levers.length, total || 0);
  const shown = max ? levers.slice(0, max) : levers;
  return (
    <span className={`flex gap-1 ${compact ? 'flex-nowrap' : 'flex-wrap'} min-w-0`}>
      {shown.map((l, i) => (
        <span key={`${l.lever}-${l.tag}-${i}`} title={l.lever ? `${l.lever} → ${l.tag}` : l.tag}
          className="inline-flex items-center gap-1 rounded-full border border-primary-300 bg-primary-50 px-2 py-0.5 text-[11px] text-grayscale-1300 max-w-full">
          {l.lever && <span className="text-grayscale-1100 whitespace-nowrap">{l.lever} →</span>}
          <span className={compact ? 'truncate max-w-[10rem]' : 'break-words'}>{l.tag || '—'}</span>
        </span>
      ))}
      {max && all > max && (
        <span className="text-[11px] text-grayscale-1100 whitespace-nowrap self-center">+{all - max}</span>
      )}
    </span>
  );
};

/** The Tech URL pages' Horizon column / drawer badge — links into Horizon data. */
export const HorizonBadge: React.FC<{ h: any; techId: string }> = ({ h, techId }) => {
  if (!h || typeof h !== 'object' || !techId) return <span className="text-grayscale-1100">—</span>;
  const has = str(h.status) === 'has';
  const levers = num(h.levers_count);
  return (
    <AdminLink to={{ group: 'horizon', tab: 'technologies', tech: techId }}
      title={`${has ? `${horizonLabel(h.horizon)}${levers ? ` · ${levers} value lever tag${levers === 1 ? '' : 's'}` : ''}`
        : 'No Horizon result yet'} — open in Horizon data`}
      className="no-underline">
      <StatusPill status={has ? 'has' : 'pending'} horizon={has ? h.horizon : undefined} />
    </AdminLink>
  );
};

/** A long text block (prompt, reasoning, raw response) — wraps, scrolls, copies. */
export const TextBlock: React.FC<{ text: any; mono?: boolean; maxHeight?: string; empty?: string }> =
  ({ text, mono, maxHeight = '24rem', empty }) => {
  const [done, setDone] = useState(false);
  const t = typeof text === 'string' ? text : text === null || text === undefined ? '' : JSON.stringify(text, null, 2);
  if (!t) return <div className="text-xs text-grayscale-1100">{empty || 'Empty.'}</div>;
  const copy = async () => {
    try { await navigator.clipboard.writeText(t); setDone(true); setTimeout(() => setDone(false), 1500); }
    catch { window.prompt('Copy this:', t.slice(0, 5000)); }
  };
  return (
    <div className="relative rounded-lg border border-grayscale-300 bg-grayscale-200 min-w-0">
      <button type="button" onClick={copy}
        className="absolute top-1.5 right-1.5 rounded px-1.5 py-0.5 text-[10px] bg-white border border-grayscale-300 text-grayscale-1100 hover:bg-grayscale-100">
        {done ? 'Copied' : 'Copy'}
      </button>
      <pre className={`p-3 pr-14 overflow-auto whitespace-pre-wrap break-words text-xs text-grayscale-1300 ${mono ? 'font-mono' : 'font-sans'}`}
        style={{ maxHeight }}>{t}</pre>
    </div>
  );
};

/** One labelled fact — the drawers' grid cells. */
export const Fact: React.FC<{ label: string; children: React.ReactNode; title?: string }> = ({ label, children, title }) => (
  <div className="rounded-lg border border-grayscale-300 bg-white px-3 py-2 min-w-0" title={title}>
    <div className="text-[10px] uppercase tracking-wide text-grayscale-1100">{label}</div>
    <div className="text-sm text-grayscale-1300 break-words">{children}</div>
  </div>
);

/** Right-side drawer shell shared by the technology and result drawers. */
export const Drawer: React.FC<{
  onClose: () => void; label: string; z?: string; width?: string; header: React.ReactNode; children: React.ReactNode;
}> = ({ onClose, label, z = 'z-40', width = '60rem', header, children }) => (
  <div className={`fixed inset-0 ${z} flex justify-end`} onClick={onClose}>
    <div className="absolute inset-0 bg-black/30" />
    <div className="relative h-full bg-grayscale-100 shadow-xl flex flex-col" style={{ width: `min(96vw, ${width})` }}
      onClick={e => e.stopPropagation()} role="dialog" aria-label={label}>
      <div className="px-5 py-4 border-b border-grayscale-300 bg-white flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">{header}</div>
        <button type="button" onClick={onClose}
          className="py-1 px-2.5 text-xs rounded-full font-grotesk font-medium whitespace-nowrap border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200">
          Close
        </button>
      </div>
      <div className="p-5 overflow-y-auto flex flex-col gap-4 min-w-0">{children}</div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Tab filters and the panel context every tab receives.
// ---------------------------------------------------------------------------
export type TechFilters = {
  q: string; university: string; industry: string; horizon: string; confidence: string;
  lever: string; tag: string; status: string; prompt: string;
};
export const TECH_EMPTY: TechFilters = {
  q: '', university: '', industry: '', horizon: '', confidence: '', lever: '', tag: '', status: 'all', prompt: '',
};

export type ResultFilters = {
  q: string; prompt: string; run: string; university: string; horizon: string; confidence: string;
  industry: string; lever: string; tag: string; error: string; reviewed: string; approved: string;
  from: string; to: string;
};
export const RESULT_EMPTY: ResultFilters = {
  q: '', prompt: '', run: '', university: '', horizon: '', confidence: '', industry: '', lever: '', tag: '',
  error: '', reviewed: '', approved: '', from: '', to: '',
};

export type PendingFilters = { q: string; university: string };
export const PENDING_EMPTY: PendingFilters = { q: '', university: '' };

/** A filter set handed to a tab from elsewhere (a KPI, a bar, a deep link); n makes each hand-over new. */
export type Preset<T> = { f: Partial<T>; n: number } | null;

export type Uni = { code: string; label: string; name: string };

export type HzCtx = {
  prompts: PromptInfo[];
  promptById: (id: any) => PromptInfo | undefined;
  runs: any[];
  universities: Uni[];
  industries: string[];
  /** Bumped after a refresh / source change so every tab re-reads. */
  dataTick: number;
  openTech: (id: string) => void;
  openResult: (id: string) => void;
  closeDrawers: () => void;
  goTab: (tab: string) => void;
  goTech: (f: Partial<TechFilters>) => void;
  goResults: (f: Partial<ResultFilters>) => void;
  goPending: (f: Partial<PendingFilters>) => void;
  goPrompt: (id: string) => void;
  reloadAll: () => void;
};

/** University select options, shortest readable label first. */
export const uniOptions = (unis: Uni[], any = 'All universities') => {
  const sorted = [...unis].sort((a, b) => (a.label || a.name || a.code).localeCompare(b.label || b.name || b.code));
  return {
    options: ['', ...sorted.map(u => u.code)],
    labels: { '': any, ...Object.fromEntries(sorted.map(u => [u.code, clip(u.label || u.name || u.code, 28)])) } as Record<string, string>,
  };
};

/** Applies a preset's filters (on top of the empty set) when a new one arrives. */
export function applyPreset<T>(preset: Preset<T>, empty: T, set: (f: T) => void, last: { current: number }) {
  if (!preset || preset.n === last.current) return;
  last.current = preset.n;
  set({ ...empty, ...preset.f });
}
